import { differenceInYears, format, parseISO, subYears } from 'date-fns'
import { t } from 'i18next'

import { IngredientUnit, MealPlan, ScaledRecipe, WeekPlan } from '@/api/types'
import { NutritionalValueCalculator } from '../NutritionFacts'
import { SORTED_MEALS } from './constants'

export function birthdateToAge(birthdate: string) {
  if (!birthdate) {
    return 0
  }
  return differenceInYears(new Date(), parseISO(birthdate))
}

export function ageToBirthdate(age: number) {
  if (!age) {
    return ''
  }

  // add 0.1 to avoid timezone issues
  return format(subYears(new Date(), age + 0.1), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'")
}

export function getIngredientQuantity(ingredient: IngredientWithQuantity) {
  let factor = ingredient.unit?.quantityInGrams || 1

  return ingredient.quantity * factor
}

export function getIngredientQuantityText(ingredient: IngredientWithQuantity) {
  let quantityInGrams = getIngredientQuantity(ingredient)
  if (ingredient.unit) {
    return `${ingredient.quantity} ${t(`measurementUnits.${ingredient.unit.name}` as any)} (${quantityInGrams}g)`
  }

  return `${Math.round(quantityInGrams)}g`
}

export type IngredientWithQuantity = {
  ingredient: {
    energy: number
  }
  quantity: number
  unit?: IngredientUnit
}

export function getIngredientEnergy(ingredient: IngredientWithQuantity) {
  return Math.round((ingredient.ingredient.energy / 100) * getIngredientQuantity(ingredient))
}

export function getRecipeQuantity(scaledRecipe: ScaledRecipe, userId: number) {
  const ingredients = scaledRecipe.scaledRecipeIngredients
  const servings = scaledRecipe.recipe.preparationDetails?.servings ?? 1

  const quantity = ingredients.reduce((acc, curr) => {
    return acc + (getIngredientQuantity(curr) * getFractionForUser(scaledRecipe, userId))
  }, 0)

  return Math.round(quantity)
}

export function getRecipesSumQuantity(scaledRecipes: ScaledRecipe[], userId: number) {
  let sum = scaledRecipes.reduce((acc, sr) => {
    return acc + getRecipeQuantity(sr, userId)
  }, 0)

  return sum
}

export function getRecipesSumEnergy(scaledRecipes: ScaledRecipe[]) {
  const sum = scaledRecipes.reduce((acc, curr) => {
    return (
      acc +
      NutritionalValueCalculator.fromRecipeIngredients(
        curr.scaledRecipeIngredients,
        curr.recipe ? curr.recipe.preparationDetails?.servings : curr.servings,
      ).energy
    )
  }, 0)

  return Math.round(sum)
}

function getFractionForUser(scaledRecipe: ScaledRecipe, userId: number) {
  let individualPortion = 1
  if (scaledRecipe.householdServings) {
    individualPortion =
      scaledRecipe.householdServings.find((s) => s.user.idUser === userId)?.portionInServings || 1
  }

  return individualPortion / (scaledRecipe.servings || 1)
}

export function getNutrientsForUser(scaledRecipes: ScaledRecipe[], userId: number) {
  const totals = {} as ReturnType<typeof NutritionalValueCalculator.fromRecipeIngredients>

  for (const scaledRecipe of scaledRecipes) {
    let nutrients = NutritionalValueCalculator.fromRecipeIngredients(
      scaledRecipe.scaledRecipeIngredients,
    )
    Object.keys(nutrients).forEach((nutri) => {
      totals[nutri] =
        (totals[nutri] || 0) + nutrients[nutri] * getFractionForUser(scaledRecipe, userId)
    })
  }

  return totals
}

export function getNutrientsPerHouseholdMember(scaledRecipes: ScaledRecipe[]) {
  const totals = {} as {
    [key: string]: ReturnType<typeof NutritionalValueCalculator.fromRecipeIngredients>
  }

  for (const scaledRecipe of scaledRecipes) {
    let nutrients = NutritionalValueCalculator.fromRecipeIngredients(
      scaledRecipe.scaledRecipeIngredients,
    )

    const householdServings = scaledRecipe.householdServings || []

    for (const householdServing of householdServings) {
      const userId = householdServing.user.firstName || 'Main User'
      if (!totals[userId]) {
        totals[userId] = {} as ReturnType<typeof NutritionalValueCalculator.fromRecipeIngredients>
      }

      Object.keys(nutrients).forEach((nutri) => {
        totals[userId][nutri] =
          (totals[userId][nutri] || 0) +
          nutrients[nutri] * (householdServing.portionInServings / scaledRecipe.servings)
      })
    }
  }

  return totals
}

export function sortByMeals(array: MealPlan[]) {
  return array.sort((a, b) => SORTED_MEALS.indexOf(a.mealOfDay) - SORTED_MEALS.indexOf(b.mealOfDay))
}


export function getNutritionDay(list: MealPlan[], userId: number) {
  if (!list)
    return {
      energy: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      fiber: 0,
    }

  const allRecipes = list.flatMap((mp) => mp.scaledRecipes)
  const nutrition = getNutrientsForUser(allRecipes, userId)

  return {
    energy: Math.round(nutrition.energy),
    protein: Math.round(nutrition.protein),
    carbs: Math.round(nutrition.carbohydrate),
    fat: Math.round(nutrition.fat),
    fiber: Math.round(nutrition.fiber || 0),
  }
}

export type IngredientRow = {
  id: number
  name: string
  englishName: string
  count: number
}

export function getWeekPlanIngredientsCount(weekPlan: WeekPlan) {
  const ingredientMap: Record<number, IngredientRow> = {}
  weekPlan.mealPlans.forEach((meal) => {
    meal.scaledRecipes.forEach((recipe) => {
      recipe.scaledRecipeIngredients.forEach((sri) => {
        ingredientMap[sri.ingredient.idIngredient] = {
          id: sri.ingredient.idIngredient,
          name: sri.ingredient.name,
          englishName: sri.ingredient.englishName || sri.ingredient.name,
          count: (ingredientMap[sri.ingredient.idIngredient]?.count || 0) + 1,
        }
      })
    })
  })
  //return sorted array of ingredients
  return Object.entries(ingredientMap)
    .map(([id, row]) => row)
    .sort((a, b) => b.count - a.count)
}
